import React, { useEffect, useState } from 'react';

import styles from './CookieConsent.module.scss';

import { OPEN_COOKIE_PREFERENCES_EVENT ,
         acceptAllCookies              ,
         refuseAllCookies              ,
         saveCookieConsent             ,
         useCookieConsent              } from '../../../utils/cookieConsent';

/**
 * Bandeau et fenêtre de préférences cookies de la régie (cf. utils/cookieConsent.ts).
 *
 * Le bandeau est posé en bas de l'écran, sans voile : la régie reste utilisable tant qu'on n'a pas
 * répondu (un direct peut être en cours), et ne pas répondre vaut refus du non-essentiel.
 * « Tout refuser » et « Tout accepter » ont exactement la même allure — refuser doit être aussi
 * simple qu'accepter. « Personnaliser » ouvre la fenêtre ; elle se rouvre à tout moment par le lien
 * « Gérer mes cookies » du pied de page.
 */
export const CookieConsent: React.FC = () => {

  const consent                 = useCookieConsent();
  const [ open , setOpen ]      = useState(false);
  const [ preferences , setPreferences ] = useState(false);
  const [ audience    , setAudience    ] = useState(false);

  const openPreferences = () => {
    setPreferences(!!consent?.preferences);
    setAudience(!!consent?.audience);
    setOpen(true);
  };

  useEffect(() => {
    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);

  const toggleRow = (title: string, text: string, checked: boolean, onChange?: (v: boolean) => void) => (
    <label className={styles.row}>
      <span className={styles.rowText}>
        <strong>{title}</strong>
        <span>{text}</span>
      </span>
      <input type="checkbox" className={styles.switch} checked={checked}
             disabled={!onChange} onChange={e => onChange?.(e.target.checked)} />
    </label>
  );

  return (
    <>
      {consent === null && !open && (
        <div className={styles.banner} role="dialog" aria-label="Cookies">
          <p className={styles.bannerText}>
            <strong>Cookies</strong>
            <br />
            La régie utilise des cookies essentiels à son fonctionnement (connexion, sécurité). Avec votre accord,
            elle mémorise aussi vos réglages d'affichage et peut mesurer son usage. Aucune publicité.
            Vous pourrez changer d'avis à tout moment avec « Gérer mes cookies ».
          </p>
          <div className={styles.bannerActions}>
            <button type="button" className={styles.btn} onClick={refuseAllCookies}>Tout refuser</button>
            <button type="button" className={styles.btn} onClick={openPreferences}>Personnaliser</button>
            <button type="button" className={styles.btn} onClick={acceptAllCookies}>Tout accepter</button>
          </div>
        </div>
      )}

      {open && (
        <div className={styles.overlay} onClick={() => setOpen(false)}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Préférences cookies" onClick={e => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h2>Gérer mes cookies</h2>
              <button type="button" className={styles.close} aria-label="Fermer" onClick={() => setOpen(false)}>×</button>
            </div>

            {toggleRow('Essentiels', 'Connexion, sécurité, fonctionnement de la régie. Toujours actifs : ils ne peuvent pas être désactivés.', true)}
            {toggleRow('Préférences', "Mémorise vos réglages d'affichage (thème, disposition, configuration des decks).", preferences, setPreferences)}
            {toggleRow("Mesure d'audience", "Statistiques d'usage de la régie. Aucune mesure n'est active aujourd'hui ; cette case donne ou retire d'avance l'accord.", audience, setAudience)}

            <div className={styles.modalActions}>
              <button type="button" className={styles.btn} onClick={() => { refuseAllCookies(); setOpen(false); }}>Tout refuser</button>
              <button type="button" className={styles.btn} onClick={() => { acceptAllCookies(); setOpen(false); }}>Tout accepter</button>
              <button type="button" className={`${styles.btn} ${styles.primary}`}
                      onClick={() => { saveCookieConsent({ preferences, audience }); setOpen(false); }}>Enregistrer mon choix</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
